<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import InstagramTemplatePreview from 'dashboard/components-next/instagram/InstagramTemplatePreview.vue';
import { instagramTemplateVariables } from 'dashboard/helper/instagramTemplateHelper';

const props = defineProps({
  template: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['sendMessage', 'resetTemplate']);

const { t } = useI18n();

const content = computed(() => props.template.content || {});
const variableKeys = computed(() => instagramTemplateVariables(content.value));

const processedParams = ref({});

const isFormInvalid = computed(() =>
  variableKeys.value.some(key => !processedParams.value[key])
);

// The body Instagram will actually render is built server-side from the
// template; this is only the text stored on the message for search and
// notifications.
const messageText = computed(() => {
  const raw = content.value.text || content.value.elements?.[0]?.title || '';
  return raw.replace(
    /\{\{(\d+)\}\}/g,
    (match, key) => processedParams.value[key] ?? match
  );
});

const sendMessage = () => {
  if (isFormInvalid.value) return;

  emit('sendMessage', {
    message: messageText.value,
    templateParams: {
      id: props.template.id,
      name: props.template.name,
      processed_params: { ...processedParams.value },
    },
  });
};
</script>

<template>
  <div class="flex flex-col w-full gap-4">
    <InstagramTemplatePreview :content="content" :variables="processedParams" />

    <div v-if="variableKeys.length" class="flex flex-col gap-3">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('INSTAGRAM_TEMPLATES.PARSER.VARIABLES_LABEL') }}
      </span>
      <Input
        v-for="key in variableKeys"
        :key="key"
        :model-value="processedParams[key]"
        :label="`{{${key}}}`"
        :placeholder="t('INSTAGRAM_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER')"
        @update:model-value="processedParams[key] = $event"
      />
    </div>

    <footer class="flex justify-end gap-2">
      <NextButton
        faded
        slate
        type="reset"
        :label="t('INSTAGRAM_TEMPLATES.PARSER.GO_BACK_LABEL')"
        @click="emit('resetTemplate')"
      />
      <NextButton
        type="button"
        :label="t('INSTAGRAM_TEMPLATES.PARSER.SEND_MESSAGE_LABEL')"
        :disabled="isFormInvalid"
        @click="sendMessage"
      />
    </footer>
  </div>
</template>
